import {supabase} from '../supabase/supabase';

const PICKUP_TOKEN_CACHE_PREFIX='sohlocal_pickup_token:';

const isNetworkError=(error)=>{
  const message=String(error?.message || '').toLowerCase();

  return message.includes('failed to send a request')
    || message.includes('failed to fetch')
    || message.includes('networkerror')
    || message.includes('network error');
};

const getTokenCacheKey=(orderId)=>{
  return `${PICKUP_TOKEN_CACHE_PREFIX}${orderId}`;
};

const readCachedPickupToken=(orderId)=>{
  if (typeof window==='undefined' || !orderId) return null;

  try {
    const cached=window.localStorage.getItem(getTokenCacheKey(orderId));

    if (!cached) return null;

    const token=JSON.parse(cached);
    const expiresAt=new Date(token?.expires_at || 0).getTime();

    if (
      !token?.qr_payload
      || !token?.pickup_code
      || !token?.expires_at
      || expiresAt<=Date.now()
    ) {
      window.localStorage.removeItem(getTokenCacheKey(orderId));
      return null;
    }

    return token;
  } catch {
    window.localStorage.removeItem(getTokenCacheKey(orderId));
    return null;
  }
};

const cachePickupToken=(orderId,token)=>{
  if (typeof window==='undefined' || !orderId || !token) return;

  try {
    window.localStorage.setItem(
      getTokenCacheKey(orderId),
      JSON.stringify(token)
    );
  } catch (error) {
    console.warn('Unable to cache pickup token:',error);
  }
};

const clearCachedPickupToken=(orderId)=>{
  if (typeof window==='undefined' || !orderId) return;

  window.localStorage.removeItem(getTokenCacheKey(orderId));
};

const readFunctionError=async(error,fallbackMessage)=>{
  let message=error?.message || fallbackMessage;
  const response=error?.context || error?.response;

  if (response && typeof response.clone==='function') {
    try {
      const payload=await response.clone().json();

      if (payload?.error) {
        message=payload.error;
      } else if (payload?.message) {
        message=payload.message;
      }
    } catch {
      try {
        const text=await response.clone().text();

        if (text) message=text;
      } catch {
        // The response may not contain readable JSON or text.
      }
    }
  }

  if (
    message==='Edge Function returned a non-2xx status code'
    || message==='FunctionsHttpError'
  ) {
    return new Error(
      'Pickup verification was rejected by the server. '
      +'Check that the QR payload belongs to this order and that the '
      +'pickup code matches exactly.'
    );
  }

  if (isNetworkError(error)) {
    return new Error(
      'The pickup Edge Function could not be reached. '
      +'Make sure redeem-pickup is deployed and configured in Supabase.'
    );
  }

  return new Error(message);
};

const invokeFunction=async(functionName,body)=>{
  const {data:sessionData}=await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error('Your session has expired. Please sign in again.');
  }

  const {data,error}=await supabase.functions.invoke(
    functionName,
    {body}
  );

  if (error) {
    throw await readFunctionError(
      error,
      `Unable to reach ${functionName}.`
    );
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data;
};

export const requestPickupToken=async(orderId)=>{
  if (!orderId) {
    throw new Error(
      'A valid order is required to create a pickup code.'
    );
  }

  const cachedToken=readCachedPickupToken(orderId);

  if (cachedToken) {
    return cachedToken;
  }

  const token=await invokeFunction(
    'pickup-token',
    {order_id:orderId}
  );

  cachePickupToken(orderId,token);

  return token;
};

export const redeemPickupToken=async({
  orderId,
  qrPayload,
  pickupCode,
  confirmationMethod='qr',
  fallbackUsed=false,
  deviceFingerprint=''
})=>{
  const normalizedPayload=String(qrPayload || '').trim();
  const normalizedCode=String(pickupCode || '')
    .replace(/[^a-z0-9]/gi,'')
    .toUpperCase();

  const result=await invokeFunction(
    'redeem-pickup',
    {
      order_id:orderId,
      qr_payload:normalizedPayload,
      pickup_code:normalizedCode,
      confirmation_method:confirmationMethod,
      fallback_used:fallbackUsed,
      device_fingerprint:deviceFingerprint
    }
  );

  if (result?.success) {
    clearCachedPickupToken(orderId);

    if (typeof window!=='undefined') {
      window.dispatchEvent(
        new CustomEvent('sohlocal:orders-changed')
      );
    }
  }

  return result;
};

export const redeemPickupFallback=async({
  orderId,
  fallbackReason,
  deviceFingerprint=''
})=>{
  const reason=String(fallbackReason || '').trim();

  if (reason.length<20) {
    throw new Error(
      'Please provide at least 20 characters explaining the fallback verification.'
    );
  }

  const result=await invokeFunction(
    'redeem-pickup-fallback',
    {
      order_id:orderId,
      fallback_reason:reason,
      device_fingerprint:deviceFingerprint
    }
  );

  if (result?.success) {
    clearCachedPickupToken(orderId);

    if (typeof window!=='undefined') {
      window.dispatchEvent(
        new CustomEvent('sohlocal:orders-changed')
      );
    }
  }

  return result;
};

export const fetchPickupOrderAddress=async(orderId)=>{
  if (!orderId) {
    throw new Error('A valid order is required to load the pickup address.');
  }

  const {data,error}=await supabase.rpc(
    'get_order_pickup_address_1793088000000',
    {p_order_id:orderId}
  );

  if (error) throw error;

  return data?.pickup_address || null;
};

export const fetchPickupNotifications=async()=>{
  const {data,error}=await supabase
    .from('pickup_notifications_1789503000000')
    .select('*')
    .order('created_at',{ascending:false})
    .limit(50);

  if (error) throw error;

  return data || [];
};

export const markPickupNotificationRead=async(notificationId)=>{
  const {data,error}=await supabase
    .from('pickup_notifications_1789503000000')
    .update({read_at:new Date().toISOString()})
    .eq('id',notificationId)
    .select()
    .single();

  if (error) throw error;

  return data;
};

export const fetchPickupOrderHistory=async(orderId)=>{
  const {data,error}=await supabase
    .from('pickup_order_history_1789503000000')
    .select('*')
    .eq('order_id',orderId)
    .order('created_at',{ascending:true});

  if (error) throw error;

  return data || [];
};

export const reportPickupProblem=async({
  orderId,
  problemType,
  description
})=>{
  const {data:userData}=await supabase.auth.getUser();

  const {data,error}=await supabase
    .from('pickup_problem_reports_1789500000000')
    .insert({
      order_id:orderId,
      reporter_id:userData.user?.id,
      problem_type:problemType,
      description
    })
    .select()
    .single();

  if (error) throw error;

  return data;
};

export const createAuthorizedPickupPerson=async({
  orderId,
  displayName,
  verificationNote=''
})=>{
  const {data:userData}=await supabase.auth.getUser();

  const {data,error}=await supabase
    .from('pickup_authorized_people_1789500000000')
    .insert({
      order_id:orderId,
      buyer_id:userData.user?.id,
      display_name:displayName.trim(),
      verification_note:verificationNote.trim()
    })
    .select(
      'id,order_id,display_name,verification_note,active,created_at'
    )
    .single();

  if (error) throw error;

  return data;
};
